/**
 * D-EZ-102 / D-EZ-95 — THE HALL OF FAME, AND EVERYONE WHO HAS LEFT THE SPORT.
 *
 * Plaques for every inductee (the bar he cleared, his numbers), the written
 * rules for getting in, and the retired list: who left, when, and how his
 * career ended — "Retired after N seasons", a lost licence, or, told plainly
 * and without decoration, a driver killed racing. Every card opens his dossier.
 */

import { useMemo } from 'react';
import {
  buildRegistry,
  HALL_OF_FAME_RULES,
  hallOfFame,
  type HistorySeason,
  type RegistryEntry,
} from '../../domain/history/index.ts';
import { formatDollars } from '../../domain/finance/money.ts';

export interface HallOfFameProps {
  seasons: readonly HistorySeason[];
  /** The season the league is in now (inductions land the season after). */
  throughSeason: number;
  onDriver?: (driverId: string) => void;
}

/** How a career ended, in plain words. */
export function careerEnding(e: RegistryEntry, deaths: ReadonlyMap<string, { seasonNumber: number; raceNumber: number }>): string {
  const seasons = e.league.seasons;
  const span = `${seasons} season${seasons === 1 ? '' : 's'}`;
  switch (e.status) {
    case 'retired':
      return `Retired after ${span}`;
    case 'licence-lost':
      return `Lost his licence after ${span}`;
    case 'died': {
      const d = deaths.get(e.driverId);
      return d ? `Killed in race ${d.raceNumber} of season ${d.seasonNumber}` : `Killed racing in season ${e.lastSeason}`;
    }
    case 'left':
      return `Left the league after ${span}`;
    default:
      return `Racing — ${span}`;
  }
}

export default function HallOfFame({ seasons, throughSeason, onDriver }: HallOfFameProps) {
  const registry = useMemo(() => buildRegistry(seasons), [seasons]);
  const hall = useMemo(() => hallOfFame(registry, throughSeason), [registry, throughSeason]);
  const deaths = useMemo(() => {
    const m = new Map<string, { seasonNumber: number; raceNumber: number }>();
    for (const s of seasons) for (const f of s.season.fatalities ?? []) m.set(f.driverId, { seasonNumber: s.seasonNumber, raceNumber: f.raceNumber });
    return m;
  }, [seasons]);
  const r = HALL_OF_FAME_RULES;
  const open = (id: string) => onDriver?.(id);

  return (
    <div className="hof">
      <section className="hof__hall" aria-labelledby="hof-h">
        <header className="hof__head">
          <p className="hof__kicker">The league&rsquo;s own</p>
          <h2 id="hof-h">Hall of Fame</h2>
          <p className="hof__rules">
            A driver goes in the season after his career ends with {r.titles} or more championships, a title and{' '}
            {r.titlePlusWins.wins} wins, {r.wins} wins, or {r.longCareer.wins} wins across {r.longCareer.starts} starts.
          </p>
        </header>
        {hall.length === 0 ? (
          <p className="hof__empty">Nobody has been inducted yet. It takes a long career at the top — and the career has to be over.</p>
        ) : (
          <ul className="hof__plaques">
            {hall.map((h) => (
              <li key={h.driverId}>
                <button type="button" className="plaque" onClick={() => open(h.driverId)}>
                  <span className="plaque__class">Class of season {h.inductedSeason}</span>
                  <span className="plaque__name">{h.name}</span>
                  <span className="plaque__citation">Inducted on {h.citation}</span>
                  <span className="plaque__stats">
                    {h.championships} title{h.championships === 1 ? '' : 's'} · {h.wins} wins · {h.starts} starts ·{' '}
                    {formatDollars(h.winningsMb)} won
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="hof__former" aria-labelledby="former-h">
        <h2 id="former-h">Retired and gone</h2>
        {registry.former.length === 0 ? (
          <p className="hof__empty">Everyone who has raced in this league is still racing.</p>
        ) : (
          <ul className="former">
            {registry.former.map((e) => (
              <li key={e.driverId} className={`former__row former__row--${e.status}`}>
                <button type="button" className="former__btn" onClick={() => open(e.driverId)}>
                  <span className="former__name">{e.name}</span>
                  <span className="former__how">{careerEnding(e, deaths)}</span>
                  <span className="former__line">
                    S{e.firstSeason}–S{e.lastSeason} · {e.career.starts} starts · {e.career.wins} wins
                    {e.career.championships > 0 ? ` · ${e.career.championships} title${e.career.championships === 1 ? '' : 's'}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
